import {formatUserDefinedObjectAdditionalColumnValue} from "~/domainModels/userDefinedObjectAdditionalColumnValues";
import {
    UserDefinedObjectTemplateAdditionalColumnType as UdoColumnType,
    type UserDefinedObjectTemplateAdditionalColumn as UdoColumn,
} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplateAdditionalColumn";
import type {CapabilityUdoColumnResolver} from "~/utilities/capabilityUdoTemplateColumns";
import {OttopilotColumnType, type OttopilotColumn} from "~/components/ottopilotDataViewer/column";

import type {OttopilotEventTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTemplate";
import type {OttopilotEventTableViewColumn} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTableViewColumn";
import type {BtlActivity} from "~/domainModels/btlActivities";
import type {User} from "@vesta/vos-types/modules/workspaceLevel/users/domain/User";
import type {UserTemplate} from "@vesta/vos-types/modules/workspaceLevel/users/domain/UserTemplate";
import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {EventWithEventContainer} from "~/utilities/apis/api";
import {
    emotoradJourneyTestRideCountCellString,
    isEmotoradJourneyTestRideCountColumnField,
    isEmotoradTestRideCountJourneyColumnDisplayName,
} from "~/utilities/emotoradJourneyTestRideCount";
import {getUserDisplayName, getUserRole, type PlatformUserDisplayDetails} from "~/utilities/utilities";
import {isGeographyAdditionalColumnType, resolveGeographyAdditionalColumnValue} from "~/utilities/geographyValueResolver";

function additionalColumnUnknownValueToString(raw: unknown): string {
    if (raw === undefined || raw === null) {
        return "";
    }
    if (typeof raw === "string") {
        return raw;
    }
    if (typeof raw === "number" || typeof raw === "boolean") {
        return String(raw);
    }
    return JSON.stringify(raw);
}

export type EventTableViewColumnTransformDeps = {
    users: Array<User>;
    userTemplates: Array<UserTemplate>;
    platformUserDetailsByUserId?: ReadonlyMap<Uuid, PlatformUserDisplayDetails>;
    btlActivities: Array<BtlActivity>;
    formatDateTime: (value: string) => string;
    getOuDisplayName?: (ouId: Uuid) => string;
    getProductCatalogueDisplayName?: (productCatalogueId: Uuid) => string;
    /** The event template — used to look up select option labels from additionalColumns */
    eventTemplate: OttopilotEventTemplate;
    /** Event Container columns used to resolve select and multi-select labels. */
    eventContainerAdditionalColumns?: Array<UdoColumn>;
    /**
     * Event `field` ids (additional column ids) whose values are user ids even if `col.type` is uuid/text.
     * Used for Dulux event owner columns mis-typed in the template.
     */
    eventColumnFieldIdsResolvedAsUserIds?: ReadonlySet<string>;
    userDefinedObjectOptionsByTemplateId?: ReadonlyMap<Uuid, ReadonlyArray<{displayName: string; id: Uuid}>>;
};

/**
 * Converts an array of OttopilotEventTableViewColumn config entries into OttopilotColumn definitions
 * with the appropriate valueGetter and valueFormatter derived from each column's type.
 */
export function eventTableViewColumnsToOttopilotColumns(
    getAdditionalColumns: CapabilityUdoColumnResolver,
    columns: Array<OttopilotEventTableViewColumn>,
    deps: EventTableViewColumnTransformDeps,
): Array<OttopilotColumn<EventWithEventContainer>> {
    const {
        users,
        userTemplates,
        platformUserDetailsByUserId,
        formatDateTime,
        getOuDisplayName,
        getProductCatalogueDisplayName,
        eventTemplate,
        eventContainerAdditionalColumns,
        eventColumnFieldIdsResolvedAsUserIds,
        userDefinedObjectOptionsByTemplateId,
    } = deps;

    return columns.map((col): OttopilotColumn<EventWithEventContainer> => {
        const base = {
            id: col.id,
            displayName: col.displayName,
            // TODO: Support sorting Geography columns by their resolved display values.
            sortingCapability: isGeographyAdditionalColumnType(col.type) ? false : col.sortingCapability,
            filteringCapability_contains: col.filteringCapability_contains,
        };

        const columnFieldId = col.field as Uuid;

        if (col.isAdditionalColumn) {
            switch (col.columnType) {
                case "OttopilotEvent": {
                    if (eventColumnFieldIdsResolvedAsUserIds?.has(String(columnFieldId)) === true) {
                        return {
                            ...base,
                            type: OttopilotColumnType.string,
                            valueGetter: (event: EventWithEventContainer): string => {
                                const userId = event.additionalColumnValues[columnFieldId] as Uuid | undefined | null;
                                if (userId === undefined || userId === null) {
                                    return "";
                                }
                                return String(userId).trim();
                            },
                            valueFormatter: (value: string): string => {
                                if (value === "") {
                                    return "-";
                                }
                                return getUserDisplayName(value as Uuid, users, userTemplates, platformUserDetailsByUserId);
                            },
                        };
                    }
                    switch (col.type) {
                        case UdoColumnType.string:
                        case UdoColumnType.indianPhoneNumber:
                        case UdoColumnType.email:
                        case UdoColumnType.uuid: {
                            return {
                                ...base,
                                type: OttopilotColumnType.string,
                                valueGetter: (event: EventWithEventContainer): string =>
                                    (event.additionalColumnValues[columnFieldId] as string | undefined | null) ?? "",
                                valueFormatter: (value: string): string => {
                                    if (value === "") {
                                        return "-";
                                    }
                                    return value;
                                },
                            };
                        }

                        case UdoColumnType.geographyCountries:
                        case UdoColumnType.geographyIndiaCities:
                        case UdoColumnType.geographyIndiaPincodes:
                        case UdoColumnType.geographyIndiaStates:
                        case UdoColumnType.geographyIndiaZones: {
                            return {
                                ...base,
                                type: OttopilotColumnType.string,
                                valueGetter: (event: EventWithEventContainer): string =>
                                    (event.additionalColumnValues[columnFieldId] as Uuid | undefined | null) ?? "",
                                valueFormatter: (value: string): string => resolveGeographyAdditionalColumnValue(col.type, value) ?? "-",
                            };
                        }

                        case UdoColumnType.geographyLatitude:
                        case UdoColumnType.geographyLongitude: {
                            return {
                                ...base,
                                type: OttopilotColumnType.string,
                                valueGetter: (event: EventWithEventContainer): string => {
                                    const value = event.additionalColumnValues[columnFieldId];
                                    return typeof value === "number" || typeof value === "string" ? String(value) : "";
                                },
                                valueFormatter: (value: string): string => resolveGeographyAdditionalColumnValue(col.type, value) ?? "-",
                            };
                        }

                        case UdoColumnType.userDefinedObject: {
                            const eventColumn = getAdditionalColumns(eventTemplate).find((item) => item.id === columnFieldId);
                            if (eventColumn?.type !== UdoColumnType.userDefinedObject) {
                                return {...base, type: OttopilotColumnType.string, valueGetter: () => ""};
                            }
                            const options = userDefinedObjectOptionsByTemplateId?.get(eventColumn.configuration.userDefinedObjectTemplateId) ?? [];
                            return {
                                ...base,
                                type: OttopilotColumnType.string,
                                valueGetter: (event: EventWithEventContainer): string => {
                                    const value = event.additionalColumnValues[columnFieldId];
                                    return typeof value === "string" ? value : "";
                                },
                                valueFormatter: (value: string): string => options.find((option) => option.id === value)?.displayName ?? "-",
                            };
                        }

                        case UdoColumnType.userDefinedObjects: {
                            const eventColumn = getAdditionalColumns(eventTemplate).find((item) => item.id === columnFieldId);
                            if (eventColumn?.type !== UdoColumnType.userDefinedObjects) {
                                return {...base, type: OttopilotColumnType.array, valueGetter: () => []};
                            }
                            const options = userDefinedObjectOptionsByTemplateId?.get(eventColumn.configuration.userDefinedObjectTemplateId) ?? [];
                            return {
                                ...base,
                                type: OttopilotColumnType.array,
                                valueGetter: (event: EventWithEventContainer): Array<string> => {
                                    const value = event.additionalColumnValues[columnFieldId];
                                    return Array.isArray(value) ? value.map((id) => options.find((option) => option.id === id)?.displayName ?? String(id)) : [];
                                },
                            };
                        }

                        case UdoColumnType.timestamp: {
                            return {
                                ...base,
                                type: OttopilotColumnType.iso8601DateTime,
                                valueGetter: (event: EventWithEventContainer): Iso8601DateTime => {
                                    const value = event.additionalColumnValues[columnFieldId];
                                    return (typeof value === "string" ? value : "") as Iso8601DateTime;
                                },
                                valueFormatter: (value: Iso8601DateTime) => formatDateTime(value),
                            };
                        }

                        case UdoColumnType.user: {
                            return {
                                ...base,
                                type: OttopilotColumnType.string,
                                valueGetter: (event: EventWithEventContainer): string => {
                                    const userId = event.additionalColumnValues[columnFieldId] as Uuid | undefined | null;
                                    if (userId === undefined || userId === null) {
                                        return "";
                                    }
                                    return userId;
                                },
                                valueFormatter: (value: string): string => {
                                    if (value === "") {
                                        return "-";
                                    }
                                    return getUserDisplayName(value as Uuid, users, userTemplates, platformUserDetailsByUserId);
                                },
                            };
                        }

                        case UdoColumnType.ou: {
                            return {
                                ...base,
                                type: OttopilotColumnType.string,
                                valueGetter: (event: EventWithEventContainer): string => {
                                    const ouId = event.additionalColumnValues[columnFieldId] as Uuid | undefined | null;
                                    if (ouId === undefined || ouId === null) {
                                        return "";
                                    }
                                    return ouId;
                                },
                                valueFormatter: (value: string): string => {
                                    if (value === "") {
                                        return "-";
                                    }
                                    return getOuDisplayName !== undefined ? getOuDisplayName(value as Uuid) : value;
                                },
                            };
                        }

                        case UdoColumnType.product: {
                            return {
                                ...base,
                                type: OttopilotColumnType.string,
                                valueGetter: (event: EventWithEventContainer): string => {
                                    const productId = event.additionalColumnValues[columnFieldId] as Uuid | undefined | null;
                                    if (productId === undefined || productId === null) {
                                        return "";
                                    }
                                    return productId;
                                },
                                valueFormatter: (value: string): string => {
                                    if (value === "") {
                                        return "-";
                                    }
                                    return getProductCatalogueDisplayName !== undefined ? getProductCatalogueDisplayName(value as Uuid) : value;
                                },
                            };
                        }

                        case UdoColumnType.userTemplate: {
                            return {
                                ...base,
                                type: OttopilotColumnType.string,
                                valueGetter: (event: EventWithEventContainer): string => {
                                    const userId = event.additionalColumnValues[columnFieldId] as Uuid | undefined | null;
                                    if (userId === undefined || userId === null) {
                                        return "";
                                    }
                                    return userId;
                                },
                                valueFormatter: (value: string): string => {
                                    if (value === "") {
                                        return "-";
                                    }
                                    return getUserRole(value as Uuid, userTemplates);
                                },
                            };
                        }

                        case UdoColumnType.number:
                        case UdoColumnType.integer: {
                            return {
                                ...base,
                                type: OttopilotColumnType.number,
                                valueGetter: (item: EventWithEventContainer): number => {
                                    const value = item.additionalColumnValues[columnFieldId];
                                    return typeof value === "number" && Number.isFinite(value) ? value : 0;
                                },
                            };
                        }
                        case UdoColumnType.boolean:
                        case UdoColumnType.image:
                        case UdoColumnType.json:
                        case UdoColumnType.ous: {
                            const metadata = getAdditionalColumns(eventTemplate).find((item) => item.id === columnFieldId);
                            return {
                                ...base,
                                type: OttopilotColumnType.string,
                                valueGetter: (item: EventWithEventContainer): string =>
                                    metadata === undefined
                                        ? additionalColumnUnknownValueToString(item.additionalColumnValues[columnFieldId])
                                        : formatUserDefinedObjectAdditionalColumnValue(metadata, item.additionalColumnValues[columnFieldId], {
                                              formatTimestamp: (value) => formatDateTime(value),
                                              referenceOptionsByTemplateId: userDefinedObjectOptionsByTemplateId,
                                              resolveUser: (id) => getUserDisplayName(id, users, userTemplates, platformUserDetailsByUserId),
                                              resolveUserTemplate: (id) => getUserRole(id, userTemplates),
                                              resolveOrganizationalUnit: (id) => getOuDisplayName?.(id) ?? "?",
                                              resolveProduct: (id) => getProductCatalogueDisplayName?.(id) ?? "?",
                                              resolveGeography: resolveGeographyAdditionalColumnValue,
                                          }),
                            };
                        }
                    }
                    throw new Error("Unsupported Event UDO column type");
                }

                case "OttopilotEventContainer": {
                    if (
                        isEmotoradJourneyTestRideCountColumnField(columnFieldId) === true ||
                        isEmotoradTestRideCountJourneyColumnDisplayName(col.displayName) === true
                    ) {
                        return {
                            ...base,
                            type: OttopilotColumnType.string,
                            valueGetter: (event: EventWithEventContainer): string =>
                                emotoradJourneyTestRideCountCellString(event.eventContainer.additionalColumnValues, columnFieldId),
                        };
                    }
                    switch (col.type) {
                        case UdoColumnType.string:
                        case UdoColumnType.indianPhoneNumber:
                        case UdoColumnType.email:
                        case UdoColumnType.uuid: {
                            return {
                                ...base,
                                type: OttopilotColumnType.string,
                                valueGetter: (event: EventWithEventContainer): string =>
                                    (event.eventContainer.additionalColumnValues[columnFieldId] as string | undefined | null) ?? "",
                                valueFormatter: (value: string): string => {
                                    if (value === "") {
                                        return "-";
                                    }
                                    return value;
                                },
                            };
                        }

                        case UdoColumnType.geographyCountries:
                        case UdoColumnType.geographyIndiaCities:
                        case UdoColumnType.geographyIndiaPincodes:
                        case UdoColumnType.geographyIndiaStates:
                        case UdoColumnType.geographyIndiaZones: {
                            return {
                                ...base,
                                type: OttopilotColumnType.string,
                                valueGetter: (event: EventWithEventContainer): string =>
                                    (event.eventContainer.additionalColumnValues[columnFieldId] as Uuid | undefined | null) ?? "",
                                valueFormatter: (value: string): string => resolveGeographyAdditionalColumnValue(col.type, value) ?? "-",
                            };
                        }

                        case UdoColumnType.geographyLatitude:
                        case UdoColumnType.geographyLongitude: {
                            return {
                                ...base,
                                type: OttopilotColumnType.string,
                                valueGetter: (event: EventWithEventContainer): string => {
                                    const value = event.eventContainer.additionalColumnValues[columnFieldId];
                                    return typeof value === "number" || typeof value === "string" ? String(value) : "";
                                },
                                valueFormatter: (value: string): string => resolveGeographyAdditionalColumnValue(col.type, value) ?? "-",
                            };
                        }

                        case UdoColumnType.integer:
                        case UdoColumnType.number: {
                            return {
                                ...base,
                                type: OttopilotColumnType.number,
                                valueGetter: (event: EventWithEventContainer): number => {
                                    const value = event.eventContainer.additionalColumnValues[columnFieldId];
                                    return typeof value === "number" && Number.isFinite(value) ? value : 0;
                                },
                            };
                        }

                        case UdoColumnType.user: {
                            return {
                                ...base,
                                type: OttopilotColumnType.string,
                                valueGetter: (event: EventWithEventContainer): string => {
                                    const userId = event.eventContainer.additionalColumnValues[columnFieldId] as Uuid | undefined | null;
                                    if (userId === undefined || userId === null) {
                                        return "";
                                    }
                                    return userId;
                                },
                                valueFormatter: (value: string): string => {
                                    if (value === "") {
                                        return "-";
                                    }
                                    return getUserDisplayName(value as Uuid, users, userTemplates, platformUserDetailsByUserId);
                                },
                            };
                        }

                        case UdoColumnType.userTemplate: {
                            return {
                                ...base,
                                type: OttopilotColumnType.string,
                                valueGetter: (event: EventWithEventContainer): string => {
                                    const userId = event.eventContainer.additionalColumnValues[columnFieldId] as Uuid | undefined | null;
                                    if (userId === undefined || userId === null) {
                                        return "";
                                    }
                                    return userId;
                                },
                                valueFormatter: (value: string): string => {
                                    if (value === "") {
                                        return "-";
                                    }
                                    return getUserRole(value as Uuid, userTemplates);
                                },
                            };
                        }

                        case UdoColumnType.userDefinedObject: {
                            const eventContainerAdditionalColumn = eventContainerAdditionalColumns?.find((item) => item.id === columnFieldId);
                            if (eventContainerAdditionalColumn === undefined || eventContainerAdditionalColumn.type !== UdoColumnType.userDefinedObject) {
                                return {...base, type: OttopilotColumnType.string, valueGetter: () => ""};
                            }
                            const options =
                                userDefinedObjectOptionsByTemplateId?.get(eventContainerAdditionalColumn.configuration.userDefinedObjectTemplateId) ?? [];
                            return {
                                ...base,
                                type: OttopilotColumnType.string,
                                valueGetter: (event: EventWithEventContainer): string => {
                                    const value = event.eventContainer.additionalColumnValues[columnFieldId];
                                    return typeof value === "string" ? value : "";
                                },
                                valueFormatter: (value: string): string => options.find((option) => option.id === value)?.displayName ?? "-",
                            };
                        }

                        case UdoColumnType.userDefinedObjects: {
                            const eventContainerAdditionalColumn = eventContainerAdditionalColumns?.find((item) => item.id === columnFieldId);
                            if (eventContainerAdditionalColumn?.type !== UdoColumnType.userDefinedObjects) {
                                return {...base, type: OttopilotColumnType.array, valueGetter: () => []};
                            }
                            const options =
                                userDefinedObjectOptionsByTemplateId?.get(eventContainerAdditionalColumn.configuration.userDefinedObjectTemplateId) ?? [];
                            return {
                                ...base,
                                type: OttopilotColumnType.array,
                                valueGetter: (event: EventWithEventContainer): Array<string> => {
                                    const value = event.eventContainer.additionalColumnValues[columnFieldId];
                                    return Array.isArray(value) ? value.map((id) => String(id)) : [];
                                },
                                valueFormatter: (value: Array<string>): Array<string> =>
                                    value.map((id) => options.find((option) => option.id === id)?.displayName ?? id),
                            };
                        }

                        case UdoColumnType.timestamp: {
                            return {
                                ...base,
                                type: OttopilotColumnType.iso8601DateTime,
                                valueGetter: (event: EventWithEventContainer): Iso8601DateTime => {
                                    const value = event.eventContainer.additionalColumnValues[columnFieldId];
                                    return (typeof value === "string" ? value : "") as Iso8601DateTime;
                                },
                                valueFormatter: (value: Iso8601DateTime) => formatDateTime(value),
                            };
                        }

                        case UdoColumnType.ou: {
                            return {
                                ...base,
                                type: OttopilotColumnType.string,
                                valueGetter: (event: EventWithEventContainer): string => {
                                    const ouId = event.eventContainer.additionalColumnValues[columnFieldId] as Uuid | undefined | null;
                                    if (ouId === undefined || ouId === null) {
                                        return "";
                                    }
                                    return ouId;
                                },
                                valueFormatter: (value: string): string => {
                                    if (value === "") {
                                        return "-";
                                    }
                                    return getOuDisplayName !== undefined ? getOuDisplayName(value as Uuid) : value;
                                },
                            };
                        }

                        case UdoColumnType.product: {
                            return {
                                ...base,
                                type: OttopilotColumnType.string,
                                valueGetter: (event: EventWithEventContainer): string => {
                                    const productId = event.eventContainer.additionalColumnValues[columnFieldId] as Uuid | undefined | null;
                                    if (productId === undefined || productId === null) {
                                        return "";
                                    }
                                    return productId;
                                },
                                valueFormatter: (value: string): string => {
                                    if (value === "") {
                                        return "-";
                                    }
                                    return getProductCatalogueDisplayName !== undefined ? getProductCatalogueDisplayName(value as Uuid) : value;
                                },
                            };
                        }

                        case UdoColumnType.boolean:
                        case UdoColumnType.image:
                        case UdoColumnType.json:
                        case UdoColumnType.ous: {
                            const metadata = eventContainerAdditionalColumns?.find((item) => item.id === columnFieldId);
                            return {
                                ...base,
                                type: OttopilotColumnType.string,
                                valueGetter: (item: EventWithEventContainer): string =>
                                    metadata === undefined
                                        ? additionalColumnUnknownValueToString(item.eventContainer.additionalColumnValues[columnFieldId])
                                        : formatUserDefinedObjectAdditionalColumnValue(metadata, item.eventContainer.additionalColumnValues[columnFieldId], {
                                              formatTimestamp: (value) => formatDateTime(value),
                                              referenceOptionsByTemplateId: userDefinedObjectOptionsByTemplateId,
                                              resolveUser: (id) => getUserDisplayName(id, users, userTemplates, platformUserDetailsByUserId),
                                              resolveUserTemplate: (id) => getUserRole(id, userTemplates),
                                              resolveOrganizationalUnit: (id) => getOuDisplayName?.(id) ?? "?",
                                              resolveProduct: (id) => getProductCatalogueDisplayName?.(id) ?? "?",
                                              resolveGeography: resolveGeographyAdditionalColumnValue,
                                          }),
                            };
                        }
                    }
                }
            }
        }

        if (col.columnType === "OttopilotEvent") {
            switch (col.field) {
                case "createdBy": {
                    return {
                        ...base,
                        type: OttopilotColumnType.string,
                        valueGetter: (event: EventWithEventContainer): string => event.createdBy,
                        valueFormatter: (value: string): string => getUserDisplayName(value as Uuid, users, userTemplates, platformUserDetailsByUserId),
                    };
                }

                case "updatedBy": {
                    return {
                        ...base,
                        type: OttopilotColumnType.string,
                        valueGetter: (event: EventWithEventContainer): string => event.updatedBy,
                        valueFormatter: (value: string): string => getUserDisplayName(value as Uuid, users, userTemplates, platformUserDetailsByUserId),
                    };
                }

                case "createdAt": {
                    return {
                        ...base,
                        type: OttopilotColumnType.iso8601DateTime,
                        valueGetter: (event: EventWithEventContainer): Iso8601DateTime => event.createdAt,
                        valueFormatter: (value: Iso8601DateTime) => formatDateTime(value),
                    };
                }

                case "updatedAt": {
                    return {
                        ...base,
                        type: OttopilotColumnType.iso8601DateTime,
                        valueGetter: (event: EventWithEventContainer): Iso8601DateTime => event.updatedAt,
                        valueFormatter: (value: Iso8601DateTime) => formatDateTime(value),
                    };
                }
            }
        }

        switch (col.field) {
            case "createdBy": {
                return {
                    ...base,
                    type: OttopilotColumnType.string,
                    valueGetter: (event: EventWithEventContainer): string => event.eventContainer.createdBy,
                    valueFormatter: (value: string): string => getUserDisplayName(value as Uuid, users, userTemplates, platformUserDetailsByUserId),
                };
            }

            case "updatedBy": {
                return {
                    ...base,
                    type: OttopilotColumnType.string,
                    valueGetter: (event: EventWithEventContainer): string => event.eventContainer.updatedBy,
                    valueFormatter: (value: string): string => getUserDisplayName(value as Uuid, users, userTemplates, platformUserDetailsByUserId),
                };
            }

            case "createdAt": {
                return {
                    ...base,
                    type: OttopilotColumnType.iso8601DateTime,
                    valueGetter: (event: EventWithEventContainer): Iso8601DateTime => event.eventContainer.createdAt,
                    valueFormatter: (value: Iso8601DateTime) => formatDateTime(value),
                };
            }

            case "updatedAt": {
                return {
                    ...base,
                    type: OttopilotColumnType.iso8601DateTime,
                    valueGetter: (event: EventWithEventContainer): Iso8601DateTime => event.eventContainer.updatedAt,
                    valueFormatter: (value: Iso8601DateTime) => formatDateTime(value),
                };
            }
        }

        return {
            ...base,
            type: OttopilotColumnType.string,
            valueGetter: (): string => "",
            valueFormatter: (): string => "-",
        };
    });
}
